import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { clusterService } from '../services/api';

const ClusterManager = ({ show, onHide, machines, onClusterCreated, editCluster }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedMachines, setSelectedMachines] = useState([]);
    const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Sync editing cluster details
    useEffect(() => {
        if (show) {
            setAlert({ show: false, message: '', variant: 'success' });
            setSearchQuery('');
            setStatusFilter('all');
            if (editCluster) {
                setName(editCluster.name || '');
                setDescription(editCluster.description || '');
                setLoading(true);
                // Fetch full cluster details to get associated machines
                clusterService.getById(editCluster.id)
                    .then(details => {
                        const mIds = details.machines.map(m => m.id);
                        setSelectedMachines(mIds);
                    })
                    .catch(err => {
                        console.error('Failed to load cluster details:', err);
                        setAlert({ show: true, message: 'Failed to load cluster machine list', variant: 'danger' });
                    })
                    .finally(() => setLoading(false));
            } else {
                setName('');
                setDescription('');
                setSelectedMachines([]);
            }
        }
    }, [show, editCluster]);

    const handleMachineSelect = (machineId, isSelected) => {
        setSelectedMachines(prev =>
            isSelected
                ? [...prev, machineId]
                : prev.filter(id => id !== machineId)
        );
    };

    const filteredMachines = machines.filter(machine => {
        const matchesSearch = 
            machine.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            machine.ip_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
            machine.mac_address.toLowerCase().includes(searchQuery.toLowerCase());
            
        let matchesStatus = true;
        if (statusFilter === 'connected') {
            matchesStatus = machine.connection_status === 'connected';
        } else if (statusFilter === 'disconnected') {
            matchesStatus = machine.connection_status !== 'connected';
        } else if (statusFilter === 'online') {
            matchesStatus = machine.is_active === 1;
        } else if (statusFilter === 'offline') {
            matchesStatus = machine.is_active !== 1;
        }
        
        return matchesSearch && matchesStatus;
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            setAlert({ show: true, message: 'Please enter a cluster name', variant: 'danger' });
            return;
        }

        try {
            const payload = {
                name,
                description,
                machineIds: selectedMachines
            };

            if (editCluster) {
                await clusterService.update(editCluster.id, payload);
            } else {
                await clusterService.create(payload);
            }

            onClusterCreated();
            onHide();

            // Reset
            setName('');
            setDescription('');
            setSelectedMachines([]);
        } catch (err) {
            console.error('Cluster save failed:', err);
            setAlert({ show: true, message: 'Operation failed: ' + (err.response?.data?.error || err.message), variant: 'danger' });
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <div className="modal-content-premium">
                <Modal.Header closeButton className="modal-header-premium border-0">
                    <Modal.Title style={{ fontWeight: 700 }}>
                        <i className="fas fa-layer-group me-2 text-primary"></i>
                        {editCluster ? 'Configure Cluster Group' : 'Create Cluster Group'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 py-3">
                    {alert.show && (
                        <Alert
                            variant={alert.variant}
                            onClose={() => setAlert({ ...alert, show: false })}
                            dismissible
                            className="border-0 mb-3"
                            style={{
                                backgroundColor: alert.variant === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(244, 63, 94, 0.15)',
                                color: alert.variant === 'success' ? '#34d399' : '#fb7185'
                            }}
                        >
                            {alert.message}
                        </Alert>
                    )}

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="text-theme-muted mt-2">Loading details...</p>
                        </div>
                    ) : (
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-label-premium text-uppercase tracking-wider">Cluster Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-input-premium w-100"
                                    placeholder="e.g. LAB-01-Machines"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-4">
                                <Form.Label className="form-label-premium text-uppercase tracking-wider">Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    className="form-input-premium w-100"
                                    placeholder="Provide description for this cluster group..."
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                />
                            </Form.Group>

                            <div className="mb-2">
                                <Form.Label className="form-label-premium text-uppercase tracking-wider">Select Associated Machines</Form.Label>
                                {machines.length === 0 ? (
                                    <div className="p-3 text-center text-theme-muted" style={{ background: 'rgba(120, 120, 120, 0.02)', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                        No registered machines found. Add machines first to associate them here.
                                    </div>
                                ) : (
                                    <>
                                        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                                            <div className="position-relative flex-grow-1">
                                                <i className="fas fa-search position-absolute text-muted" 
                                                   style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem' }}
                                                ></i>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search machines by name, IP, or MAC..."
                                                    value={searchQuery}
                                                    onChange={e => setSearchQuery(e.target.value)}
                                                    className="form-input-premium form-input-search-premium w-100"
                                                    style={{ fontSize: '0.825rem', paddingLeft: '2.5rem' }}
                                                />
                                            </div>
                                            <Form.Select
                                                value={statusFilter}
                                                onChange={e => setStatusFilter(e.target.value)}
                                                className="form-input-premium py-1"
                                                style={{ width: '160px', fontSize: '0.825rem' }}
                                            >
                                                <option value="all">All Statuses</option>
                                                <option value="connected">Agent: Connected</option>
                                                <option value="disconnected">Agent: Disconnected</option>
                                                <option value="online">Power: Online</option>
                                                <option value="offline">Power: Offline</option>
                                            </Form.Select>
                                        </div>

                                        {filteredMachines.length === 0 ? (
                                            <div className="p-4 text-center text-theme-muted" style={{ background: 'rgba(120, 120, 120, 0.02)', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                                                <i className="fas fa-search fa-2x mb-2 text-theme-muted opacity-50"></i>
                                                <p className="mb-0">No machines match your search or filter criteria.</p>
                                            </div>
                                        ) : (
                                            <div className="custom-table-container" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                                <table className="table-custom">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '48px', text: 'center' }}></th>
                                                            <th>Machine Name</th>
                                                            <th>IP Address</th>
                                                            <th>MAC Address</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredMachines.map(machine => (
                                                            <tr key={machine.id}>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        id={`assoc-mach-${machine.id}`}
                                                                        checked={selectedMachines.includes(machine.id)}
                                                                        onChange={e => handleMachineSelect(machine.id, e.target.checked)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <span className="text-theme-main font-semibold">{machine.name}</span>
                                                                        <span className={`pulse-dot-small ${machine.connection_status === 'connected' ? 'bg-success' : 'bg-danger'}`} title={machine.connection_status === 'connected' ? 'Agent Connected' : 'Agent Disconnected'} style={{ width: '6px', height: '6px', display: 'inline-block', borderRadius: '50%', verticalAlign: 'middle' }}></span>
                                                                    </div>
                                                                </td>
                                                                <td><code className="text-theme-highlight">{machine.ip_address}</code></td>
                                                                <td><code className="text-theme-muted">{machine.mac_address}</code></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer className="modal-footer-premium border-0 gap-2">
                    <Button className="btn-premium-secondary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button className="btn-premium-primary" onClick={handleSubmit} disabled={loading}>
                        {editCluster ? 'Save Changes' : 'Create Cluster'}
                    </Button>
                </Modal.Footer>
            </div>
        </Modal>
    );
};

export default ClusterManager;